import { useEffect, useRef, type FocusEvent, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ActiveNav, CommunityLink, NavSection } from "./navLinks";
import { PANEL_LINK } from "./navStyles";
import { useDismiss } from "./useDismiss";

/**
 * One section in the desktop bar (1024px and up). The label is a link to the section's hub. A section with links
 * under it also gets a chevron button that opens a panel listing the hub and those links: the "disclosure
 * navigation with top-level links" pattern (WAI-ARIA APG). It is a list of links, not an ARIA menu, so Tab moves
 * through it as usual; Down and Up arrows also move between the links, and Escape closes it.
 *
 * The section the current page belongs to is lit: foreground text and a 2px bar on the navbar's bottom edge.
 */

export default function DesktopNavItem({
  section,
  active,
  pathname,
  open,
  onOpenChange,
  community,
  align = "start",
}: {
  section: NavSection;
  active: ActiveNav | null;
  pathname: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Extra column of outside links in this section's panel (the Guides panel carries the communities). */
  community?: CommunityLink[];
  /** Which edge of the item the panel lines up with. "end" for the last items, so a wide panel stays on screen. */
  align?: "start" | "end";
}) {
  const wrapRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const focusFirst = useRef(false);
  const panelId = `nav-panel-${section.id}`;
  const hasPanel = !!section.links?.length;
  const lit = active?.sectionId === section.id;
  // "page" when this link is the page itself, "true" when the page sits under it (a compound page under Compounds).
  const currentFor = (href: string) => (active?.href === href ? (pathname === href ? "page" : "true") : undefined);

  useDismiss(open, wrapRef, (restoreFocus) => {
    onOpenChange(false);
    if (restoreFocus) buttonRef.current?.focus();
  });

  const panelLinks = () => Array.from(wrapRef.current?.querySelectorAll<HTMLAnchorElement>(`#${panelId} a`) ?? []);

  // Down arrow on a closed button opens the panel and moves to its first link once it has rendered.
  useEffect(() => {
    if (open && focusFirst.current) {
      focusFirst.current = false;
      panelLinks()[0]?.focus();
    }
  });

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const onButton = e.target === buttonRef.current;
    if (!open) {
      if (onButton && e.key === "ArrowDown") {
        e.preventDefault();
        focusFirst.current = true;
        onOpenChange(true);
      }
      return;
    }
    const links = panelLinks();
    if (!links.length) return;
    e.preventDefault();
    if (onButton) {
      links[e.key === "ArrowDown" ? 0 : links.length - 1].focus();
      return;
    }
    const i = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (i >= 0) links[(i + (e.key === "ArrowDown" ? 1 : -1) + links.length) % links.length].focus();
  };

  // Tabbing out of the item closes its panel. A click on the panel's padding moves focus nowhere (no
  // relatedTarget) and leaves it open; clicks outside are handled by useDismiss.
  const onBlur = (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (open && next && !wrapRef.current?.contains(next)) onOpenChange(false);
  };

  return (
    <li ref={wrapRef} className="relative flex h-full items-center" onKeyDown={onKeyDown} onBlur={onBlur}>
      <Link
        to={section.href}
        // The lit section is the current item of the bar: "page" on the hub itself, "true" on a page under it.
        aria-current={lit ? (pathname === section.href ? "page" : "true") : undefined}
        className={cn(
          "flex h-11 items-center whitespace-nowrap rounded-control text-sm font-medium transition-colors duration-150",
          hasPanel ? "pl-3 pr-1" : "px-3",
          lit ? "text-foreground" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {section.label}
      </Link>
      {hasPanel && (
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`More in ${section.label}`}
          onClick={() => onOpenChange(!open)}
          className={cn(
            "flex h-11 w-6 items-center justify-center rounded-control transition-colors duration-150 hover:text-foreground",
            open || lit ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", open && "rotate-180")} aria-hidden="true" />
        </button>
      )}
      {lit && <span aria-hidden="true" className="pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-foreground" />}

      <AnimatePresence>
        {open && hasPanel && (
          <motion.div
            id={panelId}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.15, ease: "easeOut" } }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.1, ease: "easeIn" } }}
            className={cn(
              "absolute top-full mt-2 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-elevated",
              align === "end" ? "right-0" : "left-0",
              community ? "grid w-[28rem] grid-cols-2 gap-2" : "w-60",
            )}
          >
            <ul aria-label={section.label}>
              {[{ label: section.hubLabel ?? section.label, href: section.href }, ...(section.links ?? [])].map((link) => {
                const current = currentFor(link.href);
                return (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      aria-current={current}
                      onClick={() => onOpenChange(false)}
                      className={cn(PANEL_LINK, current && "bg-secondary font-medium")}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
            {community && (
              <div className="border-l border-border pl-2">
                <p id={`${panelId}-community`} className="type-label px-3 pb-1 pt-3 text-muted-foreground">
                  Community
                </p>
                <ul aria-labelledby={`${panelId}-community`}>
                  {community.map((link) => (
                    <li key={link.href}>
                      <a href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => onOpenChange(false)} className={PANEL_LINK}>
                        <span className="min-w-0 flex-1 truncate">{link.label}</span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
